import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Plus, Trash, Pencil, X, ChartPie, ListBullets, Camera, TrendUp, TrendDown, Minus, Lightning, Microphone, Paperclip, Check, Prohibit, HandCoins } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { apiOrigin } from '@/utils/apiBase';
import { VoiceExpenseModal } from '@/components/expenses/VoiceExpenseModal';

const ORIGIN = apiOrigin();

const COLORS = ['#FF6B35', '#00E5FF', '#22C55E', '#F59E0B', '#3B82F6', '#A855F7', '#EC4899', '#10B981', '#F97316', '#6366F1'];

export default function ExpensesPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [tab, setTab] = useState('list');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ amount: '', category: 'Groceries', description: '', date: new Date().toISOString().slice(0, 10), vendor: '', payment_method: 'cash', paid_from: 'house_cash', receipt_path: '' });
  const [budgetForm, setBudgetForm] = useState({ category: 'Groceries', monthly_limit: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [recurring, setRecurring] = useState([]);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [recurringForm, setRecurringForm] = useState({ title: '', amount: '', category: 'Groceries', frequency: 'monthly', next_due_date: new Date().toISOString().slice(0, 10), notes: '' });
  const [statusFilter, setStatusFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [receiptView, setReceiptView] = useState(null);
  const [showVoice, setShowVoice] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [pettyCash, setPettyCash] = useState([]);
  const [showTopupForm, setShowTopupForm] = useState(false);
  const [topupForm, setTopupForm] = useState({ person: '', amount: '', note: '' });
  const [ledgers, setLedgers] = useState({});
  const [expandedLedger, setExpandedLedger] = useState('');
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const EMPTY_FORM = { amount: '', category: 'Groceries', description: '', date: new Date().toISOString().slice(0, 10), vendor: '', payment_method: 'cash', paid_from: 'house_cash', receipt_path: '' };
  const canModify = (e) => isAdmin || (e.created_by === user?.user_id && (e.status || 'approved') === 'pending');

  const fetchData = async () => {
    try {
      const [expRes, anaRes, budRes, setRes, recRes, pcRes] = await Promise.all([
        api.get(`/expenses?month=${month}`),
        api.get(`/expenses/analytics?month=${month}`),
        api.get(`/expenses/budgets?month=${month}`),
        api.get('/settings/public'),
        api.get('/expenses/recurring'),
        api.get('/expenses/petty-cash')
      ]);
      setExpenses(expRes.data);
      setAnalytics(anaRes.data);
      setBudgets(budRes.data);
      setCategories(setRes.data?.expense_categories || []);
      setRecurring(recRes.data);
      setPettyCash(pcRes.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api, month]);

  const handleSaveExpense = async (e, force = false) => {
    e?.preventDefault?.();
    try {
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.expense_id}`, { ...form, amount: Number(form.amount) });
      } else {
        await api.post('/expenses', { ...form, amount: Number(form.amount), force });
      }
      setShowForm(false);
      setEditingExpense(null);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.possible_duplicate) {
        const ex = detail.existing || {};
        if (window.confirm(`⚠️ Possible duplicate!\n\n${detail.message}\n(Filed by ${ex.paid_by || '?'}${ex.description ? ` — ${ex.description}` : ''})\n\nSave anyway?`)) {
          handleSaveExpense(e, true);
        }
      } else {
        alert(typeof detail === 'string' ? detail : 'Could not save expense');
      }
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses/budgets', { ...budgetForm, monthly_limit: Number(budgetForm.monthly_limit), month });
      setShowBudgetForm(false);
      setBudgetForm({ category: 'Groceries', monthly_limit: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleForecast = async () => {
    setForecastLoading(true);
    setTab('forecast');
    try {
      const res = await api.get('/expenses/forecast');
      setForecast(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to generate forecast');
    }
    setForecastLoading(false);
  };

  const handleReceiptScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await api.post('/expenses/receipt-ocr', { image: reader.result });
          const data = res.data;
          setForm({
            amount: data.amount || '',
            category: data.category || 'Other',
            description: data.description || '',
            date: data.date || new Date().toISOString().slice(0, 10),
            vendor: data.vendor || '',
            payment_method: 'cash',
            paid_from: 'house_cash',
            receipt_path: data.receipt_path || ''
          });
          setEditingExpense(null);
          setShowForm(true);
          setOcrLoading(false);
        } catch (err) {
          alert(err?.response?.data?.detail || 'Receipt scan failed');
          setOcrLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert('Failed to read file');
      setOcrLoading(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveRecurring = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses/recurring', { ...recurringForm, amount: Number(recurringForm.amount) });
      setShowRecurringForm(false);
      setRecurringForm({ title: '', amount: '', category: 'Groceries', frequency: 'monthly', next_due_date: new Date().toISOString().slice(0, 10), notes: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const toggleRecurring = async (id) => {
    try { await api.post(`/expenses/recurring/${id}/toggle`); fetchData(); } catch (err) { console.error(err); }
  };

  const deleteRecurring = async (id) => {
    try { await api.delete(`/expenses/recurring/${id}`); fetchData(); } catch (err) { console.error(err); }
  };

  const processRecurring = async () => {
    try {
      const res = await api.post('/expenses/recurring/process');
      if (res.data.processed > 0) { alert(`${res.data.processed} expense(s) auto-created!`); fetchData(); }
      else alert('No recurring expenses due today.');
    } catch (err) { console.error(err); }
  };

  const reviewExpense = async (expenseId, action) => {
    let note = '';
    if (action === 'reject') {
      note = window.prompt('Reason for rejection (optional):') || '';
    }
    try {
      await api.post(`/expenses/${expenseId}/review`, { action, note });
      fetchData();
    } catch (err) { alert(err?.response?.data?.detail || 'Review failed'); }
  };

  const settlePerson = async (person) => {
    if (!window.confirm(`Mark ALL outstanding own-pocket expenses of ${person} as reimbursed?`)) return;
    try {
      const res = await api.post('/expenses/settle-person', { person });
      alert(`${res.data.settled} expense(s) marked settled for ${person}`);
      fetchData();
    } catch (err) { alert(err?.response?.data?.detail || 'Settle failed'); }
  };

  const handleAttachReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await api.post('/expenses/receipt-upload', { image: reader.result });
        setForm(f => ({ ...f, receipt_path: res.data.receipt_path }));
      } catch (err) { alert('Receipt upload failed'); }
      setAttachLoading(false);
    };
    reader.readAsDataURL(file);
    if (attachInputRef.current) attachInputRef.current.value = '';
  };

  const onVoiceParsed = (parsed) => {
    setForm({
      amount: parsed.amount || '',
      category: parsed.category || 'Other',
      description: parsed.description || '',
      date: parsed.date || new Date().toISOString().slice(0, 10),
      vendor: parsed.vendor || '',
      payment_method: parsed.payment_method || 'cash',
      paid_from: parsed.paid_from || 'house_cash',
      receipt_path: ''
    });
    setEditingExpense(null);
    setShowVoice(false);
    setShowForm(true);
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses/petty-cash/topup', { ...topupForm, amount: Number(topupForm.amount) });
      setShowTopupForm(false);
      setTopupForm({ person: '', amount: '', note: '' });
      setLedgers({});
      fetchData();
    } catch (err) { alert(err?.response?.data?.detail || 'Top-up failed'); }
  };

  const toggleLedger = async (person) => {
    if (expandedLedger === person) { setExpandedLedger(''); return; }
    setExpandedLedger(person);
    if (!ledgers[person]) {
      try {
        const res = await api.get(`/expenses/petty-cash/ledger?person=${encodeURIComponent(person)}`);
        setLedgers(l => ({ ...l, [person]: res.data }));
      } catch (err) { console.error(err); }
    }
  };

  const myFloat = pettyCash.find(p => p.person === user?.name);

  const cs = 'Rs.';
  const catNames = categories.length > 0 ? categories.map(c => c.name) : ['Groceries', 'Utilities', 'Staff Salary', 'Maintenance', 'Entertainment', 'Medical', 'Education', 'Transport', 'Shopping', 'Other'];

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="expenses-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Expense Tracker</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Finances</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="month-picker"
            className="px-3 py-2 rounded-xl border border-border bg-muted/50 text-sm outline-none" />
          <input type="file" ref={fileInputRef} accept="image/*" onChange={handleReceiptScan} className="hidden" />
          <button onClick={() => setShowVoice(true)} data-testid="voice-expense-btn"
            className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center gap-2">
            <Microphone size={16} /> बोलें
          </button>
          <button onClick={() => fileInputRef.current?.click()} disabled={ocrLoading} data-testid="scan-receipt-btn"
            className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center gap-2 disabled:opacity-50">
            <Camera size={16} /> {ocrLoading ? t('scanning') : t('scan_receipt')}
          </button>
          <button onClick={handleForecast} disabled={forecastLoading} data-testid="forecast-btn"
            className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center gap-2 disabled:opacity-50">
            <Lightning size={16} /> {forecastLoading ? t('generating') : t('forecast')}
          </button>
          {isAdmin && (
            <button onClick={() => setShowBudgetForm(true)} data-testid="set-budget-btn" className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted flex items-center gap-2">
              <ChartPie size={16} /> {t('set_budget')}
            </button>
          )}
          <button onClick={() => { setShowForm(true); setEditingExpense(null); setForm(EMPTY_FORM); }}
            data-testid="add-expense-btn" className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
            <Plus size={18} /> {t('add_expense')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <p className="overline">Total Spent</p>
          <p className="text-2xl font-bold mt-1">{cs}{(analytics?.total || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="glass-card p-5">
          <p className="overline">Transactions</p>
          <p className="text-2xl font-bold mt-1">{analytics?.expense_count || 0}</p>
        </div>
        <div className="glass-card p-5" data-testid="pending-card">
          <p className="overline">Pending Approval</p>
          <p className="text-2xl font-bold mt-1">
            {analytics?.pending_count || 0}
            {analytics?.pending_total > 0 && <span className="text-sm font-medium text-amber-500 ml-2">{cs}{analytics.pending_total.toLocaleString('en-IN')}</span>}
          </p>
        </div>
        <div className="glass-card p-5" data-testid="reimburse-card">
          <p className="overline">To Reimburse</p>
          <p className="text-2xl font-bold mt-1">
            {cs}{(analytics?.reimbursements || []).reduce((a, r) => a + r.total, 0).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 w-fit">
        {['list', 'analytics', 'recurring', 'petty', 'forecast'].map(tabName => (
          <button key={tabName} onClick={() => tabName === 'forecast' ? handleForecast() : setTab(tabName)} data-testid={`expenses-tab-${tabName}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${tab === tabName ? 'bg-[#FF6B35] text-white shadow-sm' : 'text-neutral-500 hover:text-[#FF6B35]'}`}>
            {tabName === 'list' ? t('transactions') : tabName === 'analytics' ? t('analytics') : tabName === 'recurring' ? 'Recurring' : tabName === 'petty' ? 'Petty Cash' : t('forecast')}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <div className="space-y-4">
          {/* Reimbursements outstanding (admin) */}
          {isAdmin && (analytics?.reimbursements || []).length > 0 && (
            <div className="glass-card p-5" data-testid="reimbursements-panel">
              <p className="overline mb-3 flex items-center gap-2"><HandCoins size={16} /> Reimbursements Due (own pocket)</p>
              <div className="flex flex-wrap gap-3">
                {analytics.reimbursements.map(r => (
                  <div key={r.person} className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2.5" data-testid={`reimburse-${r.person.replace(/\s+/g, '_')}`}>
                    <div>
                      <p className="text-sm font-semibold">{r.person}</p>
                      <p className="text-xs text-muted-foreground">{r.count} expense{r.count !== 1 ? 's' : ''}</p>
                    </div>
                    <span className="text-base font-bold text-amber-500">{cs}{r.total.toLocaleString('en-IN')}</span>
                    <button onClick={() => settlePerson(r.person)} data-testid={`settle-${r.person.replace(/\s+/g, '_')}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/20">
                      Mark Settled
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {[['', 'All'], ['pending', `Pending${analytics?.pending_count ? ` (${analytics.pending_count})` : ''}`], ['approved', 'Approved'], ['rejected', 'Rejected']].map(([val, label]) => (
              <button key={val} onClick={() => setStatusFilter(val)} data-testid={`status-filter-${val || 'all'}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === val ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                {label}
              </button>
            ))}
            <select value={personFilter} onChange={e => setPersonFilter(e.target.value)} data-testid="person-filter"
              className="ml-auto px-3 py-1.5 rounded-full border border-border bg-muted/50 text-xs outline-none">
              <option value="">All people</option>
              {[...new Set(expenses.map(e => e.paid_by).filter(Boolean))].sort().map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Description</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Filed By</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses
                  .filter(e => !statusFilter || (e.status || 'approved') === statusFilter)
                  .filter(e => !personFilter || e.paid_by === personFilter)
                  .map(e => {
                  const status = e.status || 'approved';
                  return (
                  <tr key={e.expense_id} className={`border-b border-border last:border-0 hover:bg-muted/20 ${status === 'rejected' ? 'opacity-50' : ''}`} data-testid={`expense-row-${e.expense_id}`}>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{e.date}</td>
                    <td className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-1.5">
                        {e.description || e.vendor || '-'}
                        {e.receipt_path && (
                          <button onClick={() => setReceiptView(e.receipt_path)} title="View receipt" data-testid={`receipt-btn-${e.expense_id}`}
                            className="text-[#FF6B35] hover:scale-110 transition-transform"><Paperclip size={14} /></button>
                        )}
                      </span>
                      {e.vendor && e.description && <span className="block text-xs text-muted-foreground">{e.vendor}</span>}
                    </td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-muted font-medium whitespace-nowrap">{e.category}</span></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {e.paid_by || '-'}
                      {e.paid_from === 'own_pocket' && (
                        <span className={`block text-[10px] font-bold ${e.settled ? 'text-[#22C55E]' : 'text-amber-500'}`}>
                          {e.settled ? '✓ reimbursed' : '₹ own pocket'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${status === 'pending' ? 'bg-amber-500/10 text-amber-500' : status === 'rejected' ? 'bg-red-500/10 text-red-500' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}
                        title={e.review_note || ''} data-testid={`status-badge-${e.expense_id}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{cs}{e.amount?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isAdmin && status === 'pending' && (
                        <>
                          <button onClick={() => reviewExpense(e.expense_id, 'approve')} title="Approve" data-testid={`approve-btn-${e.expense_id}`}
                            className="p-1.5 rounded-lg bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/20"><Check size={14} weight="bold" /></button>
                          <button onClick={() => reviewExpense(e.expense_id, 'reject')} title="Reject" data-testid={`reject-btn-${e.expense_id}`}
                            className="p-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 ml-1"><Prohibit size={14} weight="bold" /></button>
                        </>
                      )}
                      {isAdmin && status === 'approved' && e.paid_from === 'own_pocket' && !e.settled && (
                        <button onClick={async () => { await api.post(`/expenses/${e.expense_id}/settle`); fetchData(); }} title="Mark reimbursed"
                          data-testid={`settle-btn-${e.expense_id}`}
                          className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20"><HandCoins size={14} /></button>
                      )}
                      {canModify(e) && (
                        <>
                          <button onClick={() => { setEditingExpense(e); setForm({ amount: e.amount, category: e.category, description: e.description, date: e.date, vendor: e.vendor, payment_method: e.payment_method || 'cash', paid_from: e.paid_from || 'house_cash', receipt_path: e.receipt_path || '' }); setShowForm(true); }} className="p-1 hover:bg-muted rounded ml-1"><Pencil size={14} /></button>
                          <button onClick={async () => { try { await api.delete(`/expenses/${e.expense_id}`); fetchData(); } catch (err) { alert(err?.response?.data?.detail || 'Delete failed'); } }} className="p-1 hover:bg-destructive/10 text-destructive rounded ml-1"><Trash size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {expenses.length === 0 && <p className="text-center text-muted-foreground py-12">No expenses for this month</p>}
          </div>
          </div>
        </div>
      )}

      {tab === 'analytics' && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="glass-card p-6">
            <p className="overline mb-4">By Category</p>
            {analytics.by_category?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={analytics.by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({category, percent}) => `${category} ${(percent * 100).toFixed(0)}%`}>
                      {analytics.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => `${cs}${v.toLocaleString('en-IN')}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-center text-muted-foreground py-8">No data</p>}
          </div>
          {/* Bar Chart */}
          <div className="glass-card p-6">
            <p className="overline mb-4">Daily Spending</p>
            {analytics.by_day?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={analytics.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={v => `${cs}${v.toLocaleString('en-IN')}`} />
                    <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-center text-muted-foreground py-8">No data</p>}
          </div>
          {/* Budget Status */}
          <div className="lg:col-span-2 glass-card p-6">
            <p className="overline mb-4">Budget Status</p>
            {analytics.budget_status?.length > 0 ? (
              <div className="space-y-4">
                {analytics.budget_status.map((b, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{b.category}</span>
                      <span className="text-muted-foreground">{cs}{b.spent.toLocaleString('en-IN')} / {cs}{b.limit.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${b.percentage > 90 ? 'bg-red-500' : b.percentage > 70 ? 'bg-yellow-500' : 'bg-primary'}`}
                        style={{ width: `${Math.min(b.percentage, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-center text-muted-foreground py-4">No budgets set for this month</p>}
          </div>
        </div>
      )}

      {/* Forecast Tab */}
      {tab === 'forecast' && (
        <div className="space-y-6">
          {forecastLoading ? (
            <div className="flex flex-col items-center py-20 gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">{t('generating')}</p>
            </div>
          ) : forecast ? (
            <>
              {/* Prediction Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass-card p-6" data-testid="forecast-total">
                  <p className="overline">{t('predicted_spending')}</p>
                  <p className="text-3xl font-bold mt-2">{cs}{(forecast.predicted_total || 0).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-muted-foreground mt-1">Based on {forecast.months_analyzed || 0} months of data</p>
                </div>
                <div className="glass-card p-6" data-testid="forecast-anomalies">
                  <p className="overline">{t('anomalies_detected')}</p>
                  <div className="mt-2 space-y-2">
                    {forecast.anomalies?.length > 0 ? forecast.anomalies.map((a, i) => (
                      <p key={i} className="text-sm text-accent">{a}</p>
                    )) : <p className="text-sm text-muted-foreground">No anomalies</p>}
                  </div>
                </div>
                <div className="glass-card p-6" data-testid="forecast-tips">
                  <p className="overline">{t('saving_tips')}</p>
                  <div className="mt-2 space-y-2">
                    {forecast.tips?.map((tip, i) => (
                      <p key={i} className="text-sm">{tip}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Category Predictions */}
              {forecast.category_predictions?.length > 0 && (
                <div className="glass-card p-6" data-testid="forecast-categories">
                  <p className="overline mb-4">{t('by_category')} — {t('forecast')}</p>
                  <div className="space-y-3">
                    {forecast.category_predictions.map((cp, i) => (
                      <div key={i} className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-sm font-medium">{cp.category}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold">{cs}{(cp.predicted || 0).toLocaleString('en-IN')}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1
                            ${cp.trend === 'up' ? 'bg-red-500/10 text-red-500' : cp.trend === 'down' ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                            {cp.trend === 'up' ? <TrendUp size={12} /> : cp.trend === 'down' ? <TrendDown size={12} /> : <Minus size={12} />}
                            {cp.trend === 'up' ? t('trend_up') : cp.trend === 'down' ? t('trend_down') : t('trend_stable')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {forecast.summary && (
                <div className="glass-card p-6" data-testid="forecast-summary">
                  <p className="overline mb-3">AI Summary</p>
                  <p className="text-sm leading-relaxed">{forecast.summary}</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground">{t('configure_openai')}</p>
            </div>
          )}
        </div>
      )}

      {/* Petty Cash Tab */}
      {tab === 'petty' && (
        <div className="space-y-4" data-testid="petty-cash-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Advances handed to staff — approved "Advance" expenses auto-deduct from their float
            </p>
            {isAdmin && (
              <button onClick={() => setShowTopupForm(true)} data-testid="topup-btn"
                className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow flex items-center gap-2">
                <Plus size={14} /> Top Up Advance
              </button>
            )}
          </div>
          {pettyCash.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-12">
              No advances given yet{isAdmin ? ' — use "Top Up Advance" to hand a float to a staff member' : ''}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pettyCash.map(p => (
                <div key={p.person} className="glass-card p-5" data-testid={`float-card-${p.person.replace(/\s+/g, '_')}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{p.person}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Total given: {cs}{(p.total_given || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-right">
                      <p className="overline">Balance</p>
                      <p className={`text-2xl font-bold ${p.balance < 0 ? 'text-red-500' : p.balance < 500 ? 'text-amber-500' : 'text-[#22C55E]'}`}
                        data-testid={`float-balance-${p.person.replace(/\s+/g, '_')}`}>
                        {cs}{(p.balance || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                  {p.balance < 0 && <p className="text-xs text-red-500 mt-1">Overspent — owes reimbursement or needs a top-up</p>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => toggleLedger(p.person)} data-testid={`ledger-toggle-${p.person.replace(/\s+/g, '_')}`}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-border hover:bg-muted">
                      {expandedLedger === p.person ? 'Hide history' : 'View history'}
                    </button>
                    {isAdmin && (
                      <button onClick={() => { setTopupForm({ person: p.person, amount: '', note: '' }); setShowTopupForm(true); }}
                        className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20">
                        + Top up
                      </button>
                    )}
                  </div>
                  {expandedLedger === p.person && (
                    <div className="mt-3 border-t border-border pt-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid={`ledger-entries-${p.person.replace(/\s+/g, '_')}`}>
                      {(ledgers[p.person] || []).length === 0 && <p className="text-xs text-muted-foreground">Loading…</p>}
                      {(ledgers[p.person] || []).map(l => (
                        <div key={l.entry_id} className="flex items-center justify-between text-xs py-1">
                          <div>
                            <span className={`font-bold uppercase text-[10px] mr-2 ${l.type === 'deduction' ? 'text-red-500' : l.type === 'refund' ? 'text-blue-400' : 'text-[#22C55E]'}`}>
                              {l.type}
                            </span>
                            <span className="text-muted-foreground">{l.note || '-'}</span>
                          </div>
                          <div className="text-right whitespace-nowrap ml-3">
                            <span className={`font-semibold ${l.type === 'deduction' ? 'text-red-500' : ''}`}>
                              {l.type === 'deduction' ? '-' : '+'}{cs}{Math.abs(l.amount).toLocaleString('en-IN')}
                            </span>
                            <span className="block text-[10px] text-muted-foreground">{(l.created_at || '').slice(0, 10)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recurring Tab */}
      {tab === 'recurring' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-500">{recurring.length} recurring expense{recurring.length !== 1 ? 's' : ''}</p>
            <div className="flex gap-2">
              <button onClick={processRecurring} data-testid="process-recurring-btn"
                className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium text-neutral-300 hover:bg-white/5 flex items-center gap-2">
                <Lightning size={14} /> Process Due
              </button>
              <button onClick={() => setShowRecurringForm(true)} data-testid="add-recurring-btn"
                className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow flex items-center gap-2">
                <Plus size={14} /> Add Recurring
              </button>
            </div>
          </div>
          {recurring.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-12">No recurring expenses set up yet</p>
          ) : (
            <div className="space-y-2">
              {recurring.map(r => (
                <div key={r.recurring_id} className="glass-card px-5 py-4 flex items-center justify-between" data-testid={`recurring-${r.recurring_id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${r.active ? 'bg-[#22C55E] shadow-[0_0_8px_rgba(34,197,94,0.5)]' : 'bg-neutral-600'}`} />
                    <div>
                      <p className="text-sm text-white font-medium">{r.title}</p>
                      <p className="text-[11px] text-neutral-500">{r.category} &middot; {r.frequency} &middot; Next: {r.next_due_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-[#FF6B35]">{cs}{r.amount?.toLocaleString('en-IN')}</span>
                    <button onClick={() => toggleRecurring(r.recurring_id)}
                      className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${r.active ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-neutral-800 text-neutral-500'}`}>
                      {r.active ? 'Active' : 'Paused'}
                    </button>
                    <button onClick={() => deleteRecurring(r.recurring_id)} className="text-neutral-600 hover:text-red-400 transition-colors">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recurring Form Modal */}
      {showRecurringForm && (
        <Modal title="Add Recurring Expense" onClose={() => setShowRecurringForm(false)}>
          <form onSubmit={handleSaveRecurring} className="space-y-4">
            <div>
              <label className="overline block mb-2">Title</label>
              <input type="text" value={recurringForm.title} onChange={e => setRecurringForm({...recurringForm, title: e.target.value})} required
                data-testid="recurring-title-input" placeholder="e.g., Monthly Rent"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Amount ({cs})</label>
                <input type="number" value={recurringForm.amount} onChange={e => setRecurringForm({...recurringForm, amount: e.target.value})} required
                  data-testid="recurring-amount-input"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Frequency</label>
                <select value={recurringForm.frequency} onChange={e => setRecurringForm({...recurringForm, frequency: e.target.value})}
                  data-testid="recurring-freq-select"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Category</label>
                <select value={recurringForm.category} onChange={e => setRecurringForm({...recurringForm, category: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50">
                  {catNames.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Next Due Date</label>
                <input type="date" value={recurringForm.next_due_date} onChange={e => setRecurringForm({...recurringForm, next_due_date: e.target.value})}
                  data-testid="recurring-date-input"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Notes (optional)</label>
              <input type="text" value={recurringForm.notes} onChange={e => setRecurringForm({...recurringForm, notes: e.target.value})}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" placeholder="e.g., Landlord bank transfer" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowRecurringForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">Cancel</button>
              <button type="submit" data-testid="save-recurring-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Add/Edit Expense Modal */}
      {showForm && (
        <Modal title={editingExpense ? 'Edit Expense' : 'Add Expense'} onClose={() => { setShowForm(false); setEditingExpense(null); }}>
          <form onSubmit={handleSaveExpense} className="space-y-4">
            <div>
              <label className="overline block mb-2">Amount ({cs})</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="expense-amount-input" required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" placeholder="0" />
            </div>
            <div>
              <label className="overline block mb-2">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="expense-category-select"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                {catNames.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Description</label>
              <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="expense-desc-input"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" placeholder="What was this for?" />
            </div>
            <div>
              <label className="overline block mb-2">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="expense-date-input"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Vendor</label>
              <input type="text" value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} data-testid="expense-vendor-input"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" placeholder="Where did you spend?" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Payment Method</label>
                <select value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})} data-testid="expense-payment-select"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Paid From</label>
                <select value={form.paid_from} onChange={e => setForm({...form, paid_from: e.target.value})} data-testid="expense-paidfrom-select"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                  <option value="house_cash">House Cash</option>
                  <option value="own_pocket">Own Pocket (अपने पैसे)</option>
                  <option value="advance">Advance</option>
                </select>
                {form.paid_from === 'advance' && myFloat && (
                  <p className={`text-xs mt-1.5 ${myFloat.balance < Number(form.amount || 0) ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="advance-balance-hint">
                    Advance balance: {cs}{(myFloat.balance || 0).toLocaleString('en-IN')}
                    {myFloat.balance < Number(form.amount || 0) && ' — not enough!'}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Receipt Photo</label>
              <input type="file" ref={attachInputRef} accept="image/*" onChange={handleAttachReceipt} className="hidden" />
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => attachInputRef.current?.click()} disabled={attachLoading} data-testid="attach-receipt-btn"
                  className="px-4 py-2.5 rounded-xl border border-white/10 text-sm hover:bg-white/5 flex items-center gap-2 disabled:opacity-50">
                  <Paperclip size={16} /> {attachLoading ? 'Uploading…' : form.receipt_path ? 'Replace photo' : 'Attach photo'}
                </button>
                {form.receipt_path && (
                  <img src={`${ORIGIN}${form.receipt_path}`} alt="receipt" onClick={() => setReceiptView(form.receipt_path)}
                    className="w-12 h-12 rounded-lg object-cover border border-white/10 cursor-pointer" data-testid="form-receipt-thumb" />
                )}
              </div>
            </div>
            <button type="submit" data-testid="save-expense-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">
              {editingExpense ? 'Update' : 'Add Expense'}
            </button>
          </form>
        </Modal>
      )}

      {/* Budget Modal */}
      {showBudgetForm && (
        <Modal title="Set Monthly Budget" onClose={() => setShowBudgetForm(false)}>
          <form onSubmit={handleSaveBudget} className="space-y-4">
            <div>
              <label className="overline block mb-2">Category</label>
              <select value={budgetForm.category} onChange={e => setBudgetForm({...budgetForm, category: e.target.value})} data-testid="budget-category-select"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                {catNames.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Monthly Limit ({cs})</label>
              <input type="number" value={budgetForm.monthly_limit} onChange={e => setBudgetForm({...budgetForm, monthly_limit: e.target.value})} data-testid="budget-limit-input" required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" placeholder="0" />
            </div>
            <p className="text-xs text-muted-foreground">Budget for: {month}</p>
            <button type="submit" data-testid="save-budget-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Set Budget</button>
          </form>
        </Modal>
      )}

      {/* Top-up Modal */}
      {showTopupForm && (
        <Modal title="Top Up Advance" onClose={() => setShowTopupForm(false)}>
          <form onSubmit={handleTopup} className="space-y-4">
            <div>
              <label className="overline block mb-2">Staff Member</label>
              <input type="text" list="petty-persons" value={topupForm.person} onChange={e => setTopupForm({...topupForm, person: e.target.value})} required
                data-testid="topup-person-input" placeholder="e.g., Ramesh (Staff)"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              <datalist id="petty-persons">
                {[...new Set([...pettyCash.map(p => p.person), ...expenses.map(e => e.paid_by).filter(Boolean)])].map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div>
              <label className="overline block mb-2">Amount ({cs})</label>
              <input type="number" value={topupForm.amount} onChange={e => setTopupForm({...topupForm, amount: e.target.value})} required
                data-testid="topup-amount-input" placeholder="e.g., 5000"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              <p className="text-[11px] text-muted-foreground mt-1">Use a negative amount to correct a mistake</p>
            </div>
            <div>
              <label className="overline block mb-2">Note (optional)</label>
              <input type="text" value={topupForm.note} onChange={e => setTopupForm({...topupForm, note: e.target.value})}
                data-testid="topup-note-input" placeholder="e.g., Monthly grocery float"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <button type="submit" data-testid="save-topup-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Add Advance</button>
          </form>
        </Modal>
      )}

      {/* Receipt viewer */}
      {receiptView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="receipt-viewer" onClick={() => setReceiptView(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div className="relative max-w-2xl w-full">
            <button onClick={() => setReceiptView(null)} data-testid="receipt-viewer-close"
              className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg"><X size={20} /></button>
            <img src={`${ORIGIN}${receiptView}`} alt="Receipt" className="w-full max-h-[85vh] object-contain rounded-2xl" />
          </div>
        </div>
      )}

      {/* Voice expense filing */}
      {showVoice && (
        <VoiceExpenseModal api={api} onParsed={onVoiceParsed} onClose={() => setShowVoice(false)} />
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
